import "./AppLoader.css";

const SIZE_PX = { large: 72, medium: 52, small: 36 };

function LoaderDots({ px }) {
  return (
    <div className="xino-brand-loader__dots" style={{ fontSize: px }} aria-hidden>
      <span className="xino-brand-loader__dot" />
      <span className="xino-brand-loader__dot" />
      <span className="xino-brand-loader__dot" />
    </div>
  );
}

function LoaderMark({ px, withOrbit }) {
  return (
    <div className="xino-brand-loader__frame" style={{ width: px, height: px }} aria-hidden>
      {withOrbit ? <div className="xino-brand-loader__orbit" /> : null}
      <div className="xino-brand-loader__x">
        <span className="xino-brand-loader__bar xino-brand-loader__bar--a" />
        <span className="xino-brand-loader__bar xino-brand-loader__bar--b" />
      </div>
    </div>
  );
}

/**
 * Xino-branded loading UI (custom CSS — not MUI CircularProgress).
 *
 * @param {boolean} [button] — compact three-dot pulse for submit buttons.
 * @param {number} [pixelSize] — fixed box size for toolbars; small sizes use dots.
 * @param {'accent'|'inherit'|'onPrimary'} [color]
 * @param {string} [message]
 * @param {'large'|'medium'|'small'} [size]
 * @param {string} [wrapperClassName]
 * @param {string|number} [minHeight]
 */
export default function AppLoader({
  button = false,
  pixelSize,
  color = "accent",
  message,
  size = "large",
  wrapperClassName,
  minHeight,
  className,
}) {
  const colorMod =
    color === "onPrimary"
      ? "xino-brand-loader--on-primary"
      : color === "inherit"
        ? "xino-brand-loader--inherit"
        : "";

  let px;
  let useDots = false;

  if (button) {
    px = 22;
    useDots = true;
  } else if (pixelSize != null) {
    px = pixelSize;
    useDots = pixelSize <= 22;
  } else {
    px = SIZE_PX[size] ?? SIZE_PX.large;
  }

  const withOrbit = !useDots && px >= 44;
  const ariaLabel = message || "Loading";

  const rootClass = [
    "xino-brand-loader",
    useDots && !message ? "xino-brand-loader--dots-only" : "",
    colorMod,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const fixedCore = (
    <div className={rootClass} role="status" aria-live="polite" aria-label={ariaLabel}>
      <span className="xino-brand-loader__sr-only">{ariaLabel}</span>
      {useDots ? <LoaderDots px={px} /> : <LoaderMark px={px} withOrbit={withOrbit} />}
      {message ? <p className="xino-brand-loader__message">{message}</p> : null}
    </div>
  );

  const hostStyle = minHeight != null ? { minHeight } : undefined;
  const hostFill = minHeight != null || wrapperClassName;
  const host = (
    <div
      className={["xino-brand-loader-host", hostFill && "xino-brand-loader-host--fill"]
        .filter(Boolean)
        .join(" ")}
      style={hostStyle}
    >
      {fixedCore}
    </div>
  );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{host}</div>;
  }

  return host;
}
